import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Calendar, Clock, User, Mail, Phone, MapPin, CheckCircle, 
  XCircle, AlertCircle, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  Filter, MoreHorizontal, Grid3X3, List, CalendarDays, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import AppointmentDetailModal from "../../components/AppointmentDetailModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: "Ausstehend", color: "bg-yellow-100 text-yellow-800", icon: AlertCircle },
  confirmed: { label: "Bestätigt", color: "bg-green-100 text-green-800", icon: CheckCircle },
  cancelled: { label: "Abgesagt", color: "bg-red-100 text-red-800", icon: XCircle },
  completed: { label: "Erschienen", color: "bg-blue-100 text-blue-800", icon: CheckCircle },
  no_show: { label: "Nicht erschienen", color: "bg-gray-100 text-gray-800", icon: XCircle }
};

const WEEKDAYS = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const WEEKDAYS_SHORT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

// Helper functions for date handling
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const getWeekDays = (startDate) => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startDate);
    day.setDate(startDate.getDate() + i);
    days.push(day);
  }
  return days;
};

const formatDateKey = (date) => {
  // Use local date components to avoid timezone issues
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Parse German date formats to match against calendar dates
const parseGermanDate = (dateStr, referenceDate = new Date()) => {
  if (!dateStr) return null;
  
  const str = dateStr.toLowerCase().trim();
  
  // Handle relative dates
  if (str === 'heute' || str === 'today') {
    return formatDateKey(referenceDate);
  }
  if (str === 'morgen' || str === 'tomorrow') {
    const tomorrow = new Date(referenceDate);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateKey(tomorrow);
  }
  
  // PRIORITY: Handle DD.MM.YYYY format (standardized format)
  const ddmmyyyyMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (ddmmyyyyMatch) {
    const day = parseInt(ddmmyyyyMatch[1]);
    const month = parseInt(ddmmyyyyMatch[2]) - 1;
    const year = parseInt(ddmmyyyyMatch[3]);
    const parsed = new Date(year, month, day);
    return formatDateKey(parsed);
  }
  
  // Handle DD.MM format (without year - use reference year)
  const ddmmMatch = str.match(/^(\d{1,2})\.(\d{1,2})$/);
  if (ddmmMatch) {
    const day = parseInt(ddmmMatch[1]);
    const month = parseInt(ddmmMatch[2]) - 1;
    const parsed = new Date(referenceDate.getFullYear(), month, day);
    return formatDateKey(parsed);
  }
  
  // Handle weekday names (Montag, Dienstag, etc.)
  const weekdayMap = {
    'montag': 1, 'mo': 1,
    'dienstag': 2, 'di': 2,
    'mittwoch': 3, 'mi': 3,
    'donnerstag': 4, 'do': 4,
    'freitag': 5, 'fr': 5,
    'samstag': 6, 'sa': 6,
    'sonntag': 0, 'so': 0
  };
  
  // Handle "Mo, 02.02" or "Montag, 02.02.2026" format (with weekday prefix)
  const shortDateMatch = str.match(/^(mo|di|mi|do|fr|sa|so|montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag),?\s*(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?/i);
  if (shortDateMatch) {
    const day = parseInt(shortDateMatch[2]);
    const month = parseInt(shortDateMatch[3]) - 1;
    const year = shortDateMatch[4] ? (shortDateMatch[4].length === 2 ? 2000 + parseInt(shortDateMatch[4]) : parseInt(shortDateMatch[4])) : referenceDate.getFullYear();
    const parsed = new Date(year, month, day);
    return formatDateKey(parsed);
  }
  
  // Handle just weekday name (find next occurrence)
  for (const [name, dayNum] of Object.entries(weekdayMap)) {
    if (str.startsWith(name)) {
      const today = new Date(referenceDate);
      const currentDay = today.getDay();
      let daysUntil = dayNum - currentDay;
      if (daysUntil <= 0) daysUntil += 7;
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + daysUntil);
      return formatDateKey(targetDate);
    }
  }
  
  // Handle ISO format (YYYY-MM-DD)
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return str.substring(0, 10);
  }
  
  // Handle German long format (10. Februar 2026)
  const longDateMatch = str.match(/(\d{1,2})\.\s*(januar|februar|märz|april|mai|juni|juli|august|september|oktober|november|dezember)\s*(\d{4})?/i);
  if (longDateMatch) {
    const day = parseInt(longDateMatch[1]);
    const monthNames = ['januar', 'februar', 'märz', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'dezember'];
    const month = monthNames.indexOf(longDateMatch[2].toLowerCase());
    const year = longDateMatch[3] ? parseInt(longDateMatch[3]) : referenceDate.getFullYear();
    if (month !== -1) {
      const parsed = new Date(year, month, day);
      return formatDateKey(parsed);
    }
  }
  
  return null; // Unknown format
};

// Calendar Day Cell Component
const CalendarDayCell = ({ date, appointments, onAppointmentClick, isToday, onShowDayDetails }) => {
  const dateKey = formatDateKey(date);
  const dayAppointments = appointments.filter(a => {
    const parsedDate = parseGermanDate(a.appointment_date);
    return parsedDate === dateKey;
  });
  
  return (
    <div 
      className={`min-h-[140px] border-r border-b border-slate-200 p-2 ${
        isToday ? 'bg-blue-50/50' : 'bg-white'
      }`}
    >
      <div className={`text-sm font-medium mb-2 ${isToday ? 'text-blue-600' : 'text-slate-500'}`}>
        {date.getDate()}
      </div>
      
      <div className="space-y-1">
        {dayAppointments.slice(0, 4).map((appt) => {
          const statusConfig = STATUS_CONFIG[appt.status] || STATUS_CONFIG.pending;
          return (
            <motion.div
              key={appt.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => onAppointmentClick(appt)}
              className={`text-xs p-1.5 rounded cursor-pointer hover:opacity-80 hover:shadow-md transition-all ${statusConfig.color}`}
              data-testid={`calendar-appointment-${appt.id}`}
            >
              <div className="font-medium truncate">{appt.appointment_time} - {appt.first_name}</div>
            </motion.div>
          );
        })}
        {dayAppointments.length > 4 && (
          <button
            onClick={() => onShowDayDetails(date, dayAppointments)}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline pl-1 cursor-pointer font-medium"
          >
            +{dayAppointments.length - 4} weitere anzeigen
          </button>
        )}
      </div>
    </div>
  );
};

// Week Calendar View Component
const WeekCalendarView = ({ appointments, currentWeekStart, onWeekChange, onAppointmentClick, onShowDayDetails }) => {
  const weekDays = useMemo(() => getWeekDays(currentWeekStart), [currentWeekStart]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return (
    <Card className="bg-white overflow-hidden" data-testid="calendar-view">
      <CardHeader className="pb-2 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-blue-600" />
            Wochenansicht
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onWeekChange(-7)}
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onWeekChange(0)}
              data-testid="today-btn"
            >
              Heute
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onWeekChange(7)}
              data-testid="next-week-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {weekDays[0].toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })} - {weekDays[6].toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </CardHeader>
      
      <CardContent className="p-0">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {weekDays.map((day, idx) => {
            const isToday = formatDateKey(day) === formatDateKey(today);
            return (
              <div 
                key={idx} 
                className={`text-center py-2 text-sm font-medium border-r border-slate-200 last:border-r-0 ${
                  isToday ? 'bg-blue-100 text-blue-700' : 'text-slate-600 bg-slate-50'
                }`}
              >
                <div>{WEEKDAYS_SHORT[day.getDay()]}</div>
                <div className={`text-lg ${isToday ? 'font-bold' : ''}`}>{day.getDate()}</div>
              </div>
            );
          })}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {weekDays.map((day, idx) => {
            const isToday = formatDateKey(day) === formatDateKey(today);
            return (
              <CalendarDayCell
                key={idx}
                date={day}
                appointments={appointments}
                onAppointmentClick={onAppointmentClick}
                isToday={isToday}
                onShowDayDetails={onShowDayDetails}
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const AppointmentCard = ({ appointment, onStatusChange, onDelete }) => {
  const statusConfig = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white font-semibold">
            {appointment.first_name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">{appointment.first_name}</h4>
            <p className="text-xs text-slate-500">{appointment.email}</p>
          </div>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onStatusChange(appointment.id, "confirmed")}>
              <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
              Als bestätigt markieren
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(appointment.id, "completed")}>
              <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
              Als erschienen markieren
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(appointment.id, "no_show")}>
              <XCircle className="w-4 h-4 mr-2 text-gray-600" />
              Nicht erschienen
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(appointment.id, "cancelled")}>
              <XCircle className="w-4 h-4 mr-2 text-red-600" />
              Absagen
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onDelete(appointment.id)}
              className="text-red-600"
            >
              Löschen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span>{appointment.appointment_date}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Clock className="w-4 h-4 text-slate-400" />
          <span>{appointment.appointment_time}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <MapPin className="w-4 h-4 text-slate-400" />
          <span>{appointment.location}</span>
        </div>
        {appointment.phone && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Phone className="w-4 h-4 text-slate-400" />
            <span>{appointment.phone}</span>
          </div>
        )}
      </div>
      
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
        <Badge className={`${statusConfig.color} flex items-center gap-1`}>
          <StatusIcon className="w-3 h-3" />
          {statusConfig.label}
        </Badge>
        {appointment.confirmation_sent && (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <Mail className="w-3 h-3" />
            E-Mail gesendet
          </span>
        )}
      </div>
    </motion.div>
  );
};

export const AdminAppointments = ({ authToken }) => {
  const [appointments, setAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]); // For calendar view
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" or "list"
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [selectedAppointment, setSelectedAppointment] = useState(null); // For detail modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Day details modal state
  const [dayDetailsOpen, setDayDetailsOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [selectedDayAppointments, setSelectedDayAppointments] = useState([]);
  
  const handleShowDayDetails = (date, dayAppointments) => {
    setSelectedDayDate(date);
    setSelectedDayAppointments(dayAppointments);
    setDayDetailsOpen(true);
  };
  
  useEffect(() => {
    fetchAppointments();
    fetchAllAppointments(); // For calendar
    fetchStats();
  }, [page, statusFilter]);
  
  const fetchAppointments = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("page_size", 20);
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      
      const response = await axios.get(`${API}/admin/appointments?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      setAppointments(response.data.appointments || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Fehler beim Laden der Termine");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch all appointments for calendar view (without pagination)
  const fetchAllAppointments = async () => {
    try {
      const response = await axios.get(`${API}/admin/appointments?page_size=500`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAllAppointments(response.data.appointments || []);
    } catch (error) {
      console.error("Error fetching all appointments:", error);
    }
  };
  
  const handleWeekChange = (days) => {
    if (days === 0) {
      setCurrentWeekStart(getWeekStart(new Date()));
    } else {
      setCurrentWeekStart(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() + days);
        return newDate;
      });
    }
  };
  
  // Open appointment detail modal
  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/appointments`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };
  
  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/admin/appointments/${appointmentId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${authToken}` }}
      );
      toast.success("Status aktualisiert");
      fetchAppointments();
      fetchStats();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Fehler beim Aktualisieren");
    }
  };
  
  const handleDelete = async (appointmentId) => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/admin/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success("Termin gelöscht");
      fetchAppointments();
      fetchAllAppointments();
      fetchStats();
    } catch (error) {
      console.error("Error deleting appointment:", error);
      toast.error("Fehler beim Löschen");
    }
  };
  
  const handleStatusChangeWithRefresh = async (appointmentId, newStatus) => {
    await handleStatusChange(appointmentId, newStatus);
    fetchAllAppointments(); // Refresh calendar data too
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Termine</h1>
          <p className="text-slate-500">Probearbeiten & Kennenlernen in Düsseldorf</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <Button
              variant={viewMode === "calendar" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("calendar")}
              className={viewMode === "calendar" ? "" : "text-slate-600"}
              data-testid="calendar-view-btn"
            >
              <CalendarDays className="w-4 h-4 mr-1" />
              Kalender
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className={viewMode === "list" ? "" : "text-slate-600"}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4 mr-1" />
              Liste
            </Button>
          </div>
          <Button onClick={() => { fetchAppointments(); fetchAllAppointments(); fetchStats(); }} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-xs text-slate-500">Gesamt</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.by_status?.pending || 0}</p>
                <p className="text-xs text-slate-500">Ausstehend</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.by_status?.confirmed || 0}</p>
                <p className="text-xs text-slate-500">Bestätigt</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.by_status?.completed || 0}</p>
                <p className="text-xs text-slate-500">Erschienen</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {(stats.by_status?.cancelled || 0) + (stats.by_status?.no_show || 0)}
                </p>
                <p className="text-xs text-slate-500">Abgesagt/No-Show</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filter */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
                <SelectItem value="confirmed">Bestätigt</SelectItem>
                <SelectItem value="completed">Erschienen</SelectItem>
                <SelectItem value="cancelled">Abgesagt</SelectItem>
                <SelectItem value="no_show">Nicht erschienen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Calendar View */}
      {viewMode === "calendar" && (
        <WeekCalendarView
          appointments={statusFilter === "all" ? allAppointments : allAppointments.filter(a => a.status === statusFilter)}
          currentWeekStart={currentWeekStart}
          onWeekChange={handleWeekChange}
          onAppointmentClick={handleAppointmentClick}
          onShowDayDetails={handleShowDayDetails}
        />
      )}
      
      {/* List View */}
      {viewMode === "list" && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : appointments.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="p-12 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Keine Termine</h3>
                <p className="text-slate-500">Es wurden noch keine Termine gebucht.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {appointments.map((appointment) => (
                  <div key={appointment.id} onClick={() => handleAppointmentClick(appointment)} className="cursor-pointer">
                    <AppointmentCard
                      appointment={appointment}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDelete}
                    />
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="flex items-center px-4 text-sm text-slate-600">
                    Seite {page} von {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
      
      {/* Appointment Detail Modal */}
      <AppointmentDetailModal
        appointment={selectedAppointment}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onStatusChange={async (id, status) => {
          await handleStatusChangeWithRefresh(id, status);
          // Update selected appointment if still viewing it
          if (selectedAppointment && selectedAppointment.id === id) {
            setSelectedAppointment(prev => ({ ...prev, status }));
          }
        }}
        onDelete={async (id) => {
          await handleDelete(id);
          handleCloseModal();
        }}
      />
      
      {/* Day Details Modal */}
      {dayDetailsOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden"
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                Termine am {selectedDayDate?.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setDayDetailsOpen(false)}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-2">
              {selectedDayAppointments.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Keine Termine an diesem Tag</p>
              ) : (
                selectedDayAppointments.map((appt) => {
                  const statusConfig = STATUS_CONFIG[appt.status] || STATUS_CONFIG.pending;
                  return (
                    <div 
                      key={appt.id}
                      onClick={() => {
                        setDayDetailsOpen(false);
                        handleAppointmentClick(appt);
                      }}
                      className={`p-3 rounded-lg cursor-pointer hover:shadow-md transition-all ${statusConfig.color}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white font-semibold text-sm">
                            {appt.first_name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{appt.first_name}</p>
                            <p className="text-xs text-slate-500">{appt.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">{appt.appointment_time}</p>
                          <p className="text-xs text-slate-500">{statusConfig.label}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminAppointments;
